import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import parseQueryStringCaseInsensitive from "@insite/client-framework/Common/Utilities/parseQueryStringCaseInsensitive";
import Zone from "@insite/client-framework/Components/Zone";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getOrderState, OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import loadOrderStatusMappings from "@insite/client-framework/Store/Data/OrderStatusMappings/Handlers/LoadOrderStatusMappings";
import { getOrderStatusMappingDataView } from "@insite/client-framework/Store/Data/OrderStatusMappings/OrderStatusMappingsSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import resetOrderNumber from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/ResetOrderNumber";
import setOrderNumber from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/SetOrderNumber";
import setVmiOrderDetailsPage from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/SetVmiOrderDetailsPage";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import { PageErrorWrapper } from "@insite/content-library/Components/PageErrorWrapper";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import displayOrder from "@tCooneyBrothers/Store/Pages/OrderDetails/Handlers/DisplayOrder";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let orderNumber;
    let orderDate;
    let isVmiOrder = false;
    if (location && location.search) {
        const parsedQuery = parseQueryStringCaseInsensitive(location.search, {
            orderNumber: "",
            orderDate: "",
            isVmiOrder: false,
        });
        orderNumber = parsedQuery.orderNumber;
        orderDate = parsedQuery.orderDate;
        isVmiOrder = parsedQuery.isVmiOrder;
    }

    const orderState = getOrderState(state, orderNumber);
    const orderStatusMappingDataView = getOrderStatusMappingDataView(state);
    const isOrderNumberStoredInState = state.pages.orderDetails.orderNumber === orderNumber;

    return {
        location,
        orderNumber,
        orderDate,
        orderState,
        shouldDisplayOrder: !isOrderNumberStoredInState,
        shouldLoadOrderStatusMappings: !orderStatusMappingDataView.value && !orderStatusMappingDataView.isLoading,
        isVmiOrder,
        isVmiOrderDetailsPage: state.pages.orderDetails.isVmiOrderDetailsPage,
        isOrderNumberStoredInState,
    };
};

const mapDispatchToProps = {
    setOrderNumber,
    displayOrder,
    loadOrderStatusMappings,
    setVmiOrderDetailsPage,
    resetOrderNumber,
};

type Props = ResolveThunks<typeof mapDispatchToProps> & PageProps & ReturnType<typeof mapStateToProps>;

export interface OrderDetailsPageStyles {
    loadFailedWrapper?: InjectableCss;
    loadFailedText?: TypographyPresentationProps;
    mainPage?: MobiusPageProps;
}

export const orderDetailsPageStyles: OrderDetailsPageStyles = {
    mainPage: {
        css: css`
            [class^="GridOffset"] {
                width: 100%;
            }
        `,
    },
    loadFailedWrapper: {
        css: css`
            display: flex;
            height: 200px;
            justify-content: center;
            align-items: center;
            background-color: ${getColor("common.accent")};
        `,
    },
    loadFailedText: { weight: "bold" },
};

const formatOrderDate = (orderDate: Date) => orderDate.toISOString().slice(0, 10);

class OrderDetailsPage extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const {
            shouldLoadOrderStatusMappings,
            loadOrderStatusMappings,
            orderNumber,
            shouldDisplayOrder,
            displayOrder,
            isVmiOrder,
            isVmiOrderDetailsPage,
            setVmiOrderDetailsPage,
            orderDate,
            orderState,
        } = this.props;
        // The share entity (for orders) functionality can use this page
        // to render HTML for PDF generation, so we need to leave this SSR
        // ability so that functionality works.
        if (shouldLoadOrderStatusMappings) {
            loadOrderStatusMappings();
        }
        if (orderNumber && shouldDisplayOrder) {
            displayOrder({
                orderNumber,
                orderDate:
                    typeof orderDate === "string"
                        ? orderDate
                        : orderDate
                          ? formatOrderDate(orderDate)
                          : orderState.value?.orderDate && formatOrderDate(orderState.value.orderDate),
                expand: isVmiOrder ? ["orderLines", "shipments", "vmidetails"] : ["orderLines", "shipments"],
            });
        }
        if (isVmiOrderDetailsPage) {
            setVmiOrderDetailsPage(false);
        }
    }

    componentDidUpdate(): void {
        const { orderNumber, isOrderNumberStoredInState, setOrderNumber } = this.props;
        if (orderNumber && !isOrderNumberStoredInState) {
            setOrderNumber({ orderNumber });
        }
    }

    componentWillUnmount() {
        const { resetOrderNumber } = this.props;
        resetOrderNumber();
    }

    render() {
        const styles = orderDetailsPageStyles;
        const { orderState, orderNumber, id } = this.props;

        // During share entity SSR (PDF generation), ensure we don't render content
        // until order data is fully loaded. This prevents blank PDFs caused by
        // rendering before data arrives. The SSR loop will re-render once data loads.
        if (getIsShareEntity() && orderNumber && (!orderState.value || orderState.isLoading)) {
            return (
                <Page>
                    <div>Loading order {orderNumber}...</div>
                </Page>
            );
        }

        return (
            <Page {...styles.mainPage}>
                <PageErrorWrapper
                    value={orderState}
                    badRequestMessage={siteMessage("Order_BadRequest")}
                    notAuthorizedMessage={siteMessage("Order_NotAuthorized")}
                    notFoundMessage={siteMessage("Order_NotFound")}
                    extendedStyles={orderDetailsPageStyles}
                    loadFailedTextTestSelector="orderDetails_pageNotFound"
                >
                    <OrderStateContext.Provider value={orderState}>
                        <Zone contentId={id} zoneName="Content" />
                    </OrderStateContext.Provider>
                </PageErrorWrapper>
                <Modals />
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderDetailsPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};
export default pageModule;

/**
 * @deprecated Use string literal "OrderDetailsPage" instead of this constant.
 */
export const OrderDetailsPageContext = "OrderDetailsPage";
